// Per-ticket response-time computation for the admin response report.
//
// PURITY CONTRACT: ticket.messages is a MobX observable array owned by supportTicketStore
// and is rendered live by the ticket view. This module must never sort/reverse/splice/push
// it or reassign into it. Everything below builds new local arrays and objects.
//
// Party rules (order matters — AI replies carry isStaffReply: true, and system messages
// carry isStaffReply: false, so naive splitting on isStaffReply is wrong both ways):
//   isAiGenerated    -> 'ai'      (never counted; does not stop the clock)
//   isSystemMessage  -> 'system'  (never counted; must not be read as a customer message)
//   isStaffReply     -> 'staff'
//   otherwise        -> 'customer'  (includes isOnBehalfOfCustomer phone logs)

export const PARTY = {
    STAFF: 'staff',
    CUSTOMER: 'customer',
    AI: 'ai',
    SYSTEM: 'system',
};

const UNKNOWN_NAME = 'Ukjent';

const toDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const partyOf = (message) => {
    if (message.isAiGenerated) return PARTY.AI;
    if (message.isSystemMessage) return PARTY.SYSTEM;
    if (message.isStaffReply === true) return PARTY.STAFF;
    return PARTY.CUSTOMER;
};

const nameOf = (message) => message.fromName || message.addedByUserName || UNKNOWN_NAME;

const statsOf = (values) => {
    if (values.length === 0) {
        return { count: 0, avgMs: null, minMs: null, maxMs: null };
    }
    const sum = values.reduce((acc, value) => acc + value, 0);
    return {
        count: values.length,
        avgMs: Math.round(sum / values.length),
        minMs: Math.min(...values),
        maxMs: Math.max(...values),
    };
};

const emptyReport = () => ({
    turns: [],
    staff: statsOf([]),
    customer: statsOf([]),
    perPerson: [],
    totals: {
        firstHumanResponseMs: null,
        openMs: null,
        aiReplyCount: 0,
        systemMessageCount: 0,
        undatedMessageCount: 0,
    },
});

/**
 * Build the response report for one ticket.
 *
 * @param {Object} ticket - support ticket (may be undefined/malformed)
 * @returns {Object} report — see the plan/spec for the full shape
 */
export function responseReport(ticket) {
    const messages = Array.isArray(ticket && ticket.messages) ? ticket.messages : [];
    const createdDate = toDate(ticket && ticket.createdDate);

    let aiReplyCount = 0;
    let systemMessageCount = 0;
    let undatedMessageCount = 0;

    // New array of new objects — ticket.messages itself is never touched.
    const rows = [];
    messages.forEach((message) => {
        if (!message) return;
        const party = partyOf(message);
        if (party === PARTY.AI) aiReplyCount += 1;
        if (party === PARTY.SYSTEM) systemMessageCount += 1;
        const date = toDate(message.sentDate);
        if (!date) {
            undatedMessageCount += 1;
            return;
        }
        rows.push({ message, party, date });
    });

    if (rows.length === 0) {
        const report = emptyReport();
        report.totals.aiReplyCount = aiReplyCount;
        report.totals.systemMessageCount = systemMessageCount;
        report.totals.undatedMessageCount = undatedMessageCount;
        return report;
    }

    // Safe: `rows` is local. Stable sort keeps same-timestamp messages in arrival order.
    rows.sort((a, b) => a.date - b.date);

    // Collapse consecutive COUNTED same-party messages into one turn. Uncounted rows
    // (ai/system) are emitted in place but deliberately do NOT break a turn.
    const turns = [];
    let currentCounted = null;
    rows.forEach((row) => {
        const counted = row.party === PARTY.STAFF || row.party === PARTY.CUSTOMER;
        if (!counted) {
            turns.push({
                index: null,
                party: row.party,
                counted: false,
                name: nameOf(row.message),
                loggedBy: null,
                inputSource: null,
                date: row.date,
                messageCount: 1,
                responseMs: null,
            });
            return;
        }
        if (currentCounted && currentCounted.party === row.party) {
            currentCounted.messageCount += 1;
            return; // keep the FIRST message's date — the clock starts when they went unanswered
        }
        const onBehalf = !!row.message.isOnBehalfOfCustomer;
        const turn = {
            index: null,
            party: row.party,
            counted: true,
            name: nameOf(row.message),
            loggedBy: onBehalf ? (row.message.addedByUserName || null) : null,
            inputSource: onBehalf ? (row.message.inputSource || null) : null,
            date: row.date,
            messageCount: 1,
            responseMs: null,
        };
        turns.push(turn);
        currentCounted = turn;
    });

    // Number the counted turns and measure each against the previous counted turn.
    let previous = null;
    let counter = 0;
    turns.forEach((turn) => {
        if (!turn.counted) return;
        counter += 1;
        turn.index = counter;
        if (previous) {
            // Non-negative by construction: `rows` was sorted chronologically above.
            turn.responseMs = turn.date - previous.date;
        }
        previous = turn;
    });

    const durationsFor = party => turns
        .filter(turn => turn.counted && turn.party === party && turn.responseMs !== null)
        .map(turn => turn.responseMs);

    const perPersonMap = new Map();
    turns.forEach((turn) => {
        if (!turn.counted || turn.party !== PARTY.STAFF || turn.responseMs === null) return;
        const list = perPersonMap.get(turn.name) || [];
        list.push(turn.responseMs);
        perPersonMap.set(turn.name, list);
    });
    const perPerson = Array.from(perPersonMap.entries())
        .map(([name, values]) => ({ name, ...statsOf(values) }))
        .sort((a, b) => b.avgMs - a.avgMs);

    const firstStaffTurn = turns.find(turn => turn.counted && turn.party === PARTY.STAFF) || null;
    let firstHumanResponseMs = null;
    if (firstStaffTurn && createdDate) {
        // For email-imported tickets, createdDate is the IMPORT time while the first message
        // carries the original email's Date header, so createdDate can postdate it by hours.
        // Start the clock at whichever is earlier so this figure never contradicts the first
        // row of the very same table.
        const start = Math.min(createdDate.getTime(), rows[0].date.getTime());
        const raw = firstStaffTurn.date - start;
        firstHumanResponseMs = raw < 0 ? 0 : raw;
    }

    const openMs = rows.length > 1
        ? rows[rows.length - 1].date - rows[0].date
        : null;

    return {
        turns,
        staff: statsOf(durationsFor(PARTY.STAFF)),
        customer: statsOf(durationsFor(PARTY.CUSTOMER)),
        perPerson,
        totals: {
            firstHumanResponseMs,
            openMs,
            aiReplyCount,
            systemMessageCount,
            undatedMessageCount,
        },
    };
}
